import type {ReactElement} from 'react';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import {getCreditCostForVoiceId, getPromptBoxOptions} from '@Panels/ai-voice-panel/ai-voice-panel.library';
import {AiVoicePanelErrorState} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {GeneratedVoiceList} from '@Panels/ai-voice-panel/components/generated-voice-list';
import {DailyLimitError} from '@Panels/ai-voice-panel/components/daily-limit-error';
import type {AiVoiceViewProps} from './ai-voice-view.types';
import {VoiceLanguageSelector} from '@Panels/ai-voice-panel/components/voice-language-selector/voice-language-selector';
import {VoiceProfileSelector} from '@Panels/ai-voice-panel/components/voice-profile-selector/voice-profile-selector';
import styles from './ai-voice-view.module.scss';

export function AiVoiceView({
  panelId,
  isGenerating,
  errorState,
  selectedVoiceProfileId,
  selectedLanguage,
  onCTAClick,
  onAddAudioItem,
  onProfileSelected,
  onLanguageSelected,
  getAiPromptBoxOptions,
  isFullScreen = false,
  enableDragToCanvas = false,
  maxSelectionNumber,
}: AiVoiceViewProps): ReactElement<HTMLDivElement> {
  const getVoiceSelectors = (): ReactElement => {
    return (
      <div className={'flexbox flex-items-center gap-2 spacing-m-t-3'}>
        <VoiceLanguageSelector selectedLanguage={selectedLanguage} onLanguageSelected={onLanguageSelected} />
        <VoiceProfileSelector language={selectedLanguage} selectedProfileId={selectedVoiceProfileId} onProfileSelected={onProfileSelected} />
      </div>
    );
  };

  return (
    <div className={`flex-v-row _full-height`}>
      <AiPromptBox
        id={panelId}
        inputTestId={PosterEditorTestId.AI_VOICE_PROMPT_INPUT}
        blockClasses={styles.promptBox}
        inputClasses={styles.promptBoxInput}
        initialConfig={getPromptBoxOptions(getCreditCostForVoiceId(selectedVoiceProfileId))}
        onGenerate={onCTAClick}
        onRegenerate={onCTAClick}
        options={[getAiPromptBoxOptions]}
      />
      {getVoiceSelectors()}
      <GeneratedVoiceList
        isGenerating={isGenerating}
        enableDragToCanvas={enableDragToCanvas}
        onAddAudioItem={onAddAudioItem}
        isFullScreen={isFullScreen}
        maxSelectionNumber={maxSelectionNumber}
      />

      {errorState === AiVoicePanelErrorState.DAILY_LIMIT_REACHED ? <DailyLimitError /> : null}
    </div>
  );
}
